import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import RunAgentModal from '../components/RunAgentModal';
import OperatorChatModal from '../components/OperatorChatModal';
import { AgentStatusIndicator, deriveOperationalStatus } from '../components/AgentStatusIndicator';
import { WorkspaceHeader, WorkspaceContent, EmptyState } from '../components/WorkspaceLayout';

import {
  ChatText,
  CheckCircle,
  Clock,
  Eye,
  GearSix,
  Info,
  Lightning,
  ListBullets,
  Lock,
  Play,
  Robot,
  Scales,
  Shield,
  SquaresFour,
  UsersThree,
  WarningCircle
} from '@phosphor-icons/react';
import { AgentAvatar } from '../components/AgentAvatar';
const SENSITIVE_BADGE_ICONS = {
  'Finance': { icon: Lock, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  'HR': { icon: UsersThree, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  'Legal': { icon: Scales, color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  'Compliance': { icon: Shield, color: 'bg-green-500/20 text-green-400 border-green-500/30' }
};

/**
 * My Agents Page - Phase 11: Employee Agent Access
 * Employees view and run assigned agents
 */
const MyAgents = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  // Note: agentToRun handling moved to global GlobalRunAgentModal in App.js
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seatRequired, setSeatRequired] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (activeOrganization) {
      fetchMyAgents();
      fetchMyOperators();
    }
  }, [activeOrganization]);

  const [operators, setOperators] = useState([]);
  const [selectedOperatorForChat, setSelectedOperatorForChat] = useState(null);

  const fetchMyAgents = async () => {
    try {
      setLoading(true);
      // Use unified Worker API
      const res = await fetchWithOrg(`${BACKEND_URL}/api/workers`, {}, activeOrganization?.organization_id);
      if (res.ok) {
        const data = await res.json();
        const allWorkers = (data.workers || []).map(w => ({
          ...w,
          agent_id: w.id,
          description: w.configuration?.description || '',
          short_description: w.configuration?.short_description || '',
          is_active: w.status === 'active',
          accepted_inputs: w.capabilities?.length > 0 ? w.capabilities : ['text'],
          // Keep capability_profile as string for AgentAvatar compatibility
          capability_profile: w.configuration?.capability_profile || w.type || '',
          total_runs: w.run_count || 0,
          organization_id: w.metadata?.org_id || activeOrganization?.organization_id,
        }));
        // Separate agents from operators for display
        const regularAgents = allWorkers.filter(w => w.type !== 'operator');
        const operatorsList = allWorkers.filter(w => w.type === 'operator');
        setAgents(regularAgents);
        setOperators(operatorsList);
      }
    } catch (err) {
      console.error('Error fetching workers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyOperators = async () => {
    // Operators are now fetched together with agents above
    // This function is kept for backward compatibility
  };

  const getReadinessInfo = (state, agentStatus) => {
    // If agent is active and no specific readiness state, assume it's ready
    if (!state && agentStatus === 'active') {
      return { icon: CheckCircle, color: 'text-green-400', text: 'Ready' };
    }
    
    switch (state) {
      case 'ready':
        return { icon: CheckCircle, color: 'text-green-400', text: 'Ready' };
      case 'needs_attention':
        return { icon: WarningCircle, color: 'text-yellow-400', text: 'Needs Attention' };
      case 'not_configured':
        return { icon: Info, color: 'text-blue-400', text: 'Setup Required' };
      default:
        // For draft or unknown states, show Active instead of Unknown
        return { icon: CheckCircle, color: 'text-green-400', text: 'Active' };
    }
  };

  const handleRunAgent = (agent) => {
    setSelectedAgent(agent);
    setModalOpen(true);
  };

  const handleSessionSuccess = (sessionId) => {
    // Don't navigate immediately - let the success modal show first
    // User can click "View Results" to navigate to the session
    console.log('Session completed:', sessionId);
  };

  const handleViewSessions = (agentId) => {
    navigate(`/partner-pro/workspace/sessions?agent_id=${agentId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="h-8 bg-gray-700 rounded w-48 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-700 rounded w-64 animate-pulse"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)] animate-pulse">
                <div className="w-12 h-12 bg-gray-700 rounded-xl mb-4"></div>
                <div className="h-6 bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-full mb-4"></div>
                <div className="h-10 bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (seatRequired) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md text-center border border-[rgba(255,255,255,0.1)]">
          <WarningCircle size={64} className="text-yellow-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-3">Employee Seat Required</h2>
          <p className="text-gray-300 mb-6">
            You need an active employee seat to run agents. Please contact your administrator to activate your seat.
          </p>
          <div className="text-sm text-gray-400">
            <p>💡 Your organization may need to upgrade their plan or purchase additional seats.</p>
          </div>
        </div>
      </div>
    );
  }

  if (agents.length === 0 && operators.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">My Workers</h1>
            <p className="text-gray-400">Run and manage your assigned workers</p>
          </div>

          <div className="glass rounded-2xl p-12 text-center border border-[rgba(255,255,255,0.1)]">
            <div className="w-20 h-20 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-6">
              <Lightning size={40} className="text-blue-400" />
            </div>
            <h3 className="text-2xl font-semibold text-white mb-3">No workers assigned yet</h3>
            <p className="text-gray-400 mb-4 max-w-md mx-auto">
              For privacy, agents must be assigned by an admin before you can use them.
            </p>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              Contact your admin to request access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <WorkspaceHeader
          icon={Robot}
          title="My Workers"
          description="Run and manage your assigned workers"
        />

        {/* Agent Cards Grid */}
        {agents.length > 0 && (
          <>
            <h2 className="text-xl font-semibold text-white mb-4">Workers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {agents.map((agent) => {
                const readiness = getReadinessInfo(agent.readiness_state, agent.status);
                const ReadinessIcon = readiness.icon;

                return (
                  <div
                    key={agent.id}
                    className="rounded-[10px] p-5 bg-[var(--surface-2)] border border-[var(--border-default)] hover:border-[var(--border-strong)] hover:shadow-[var(--shadow-md)] transition-all group flex flex-col h-full"
                  >
                    {/* Avatar & Readiness */}
                    <div className="flex items-start justify-between mb-4">
                      <AgentAvatar name={agent.name} size="md" agentType={agent.agent_type || agent.agentType || agent.capability_profile} iconUrl={agent.icon_url} agent={agent} />
                      <AgentStatusIndicator
                        status={
                          (agent.readiness_state === 'ready' || (!agent.readiness_state && agent.status === 'active'))
                            ? 'active'
                            : agent.readiness_state === 'needs_attention'
                              ? 'error'
                              : 'draft'
                        }
                        variant="badge"
                        size="xs"
                      />
                    </div>

                    {/* Name & Description */}
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-white group-hover:text-[#6366F1] transition-colors">
                        {agent.name}
                      </h3>
                      <AgentStatusIndicator status={deriveOperationalStatus(agent)} variant="dot" size="sm" />
                    </div>
                    <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                      {agent.description}
                    </p>

                    {/* Tags & Sensitive Badge - flex-grow to push actions to bottom */}
                    <div className="flex flex-wrap gap-2 mb-4 flex-grow">
                      {agent.sensitive_badge && (() => {
                        const badgeConfig = SENSITIVE_BADGE_ICONS[agent.sensitive_badge.label];
                        const BadgeIcon = badgeConfig?.icon;
                        return (
                          <span className={`text-xs px-2 py-1 rounded border flex items-center gap-1 h-fit ${badgeConfig?.color}`}>
                            {BadgeIcon && <BadgeIcon size={12} />}
                            {agent.sensitive_badge.icon} {agent.sensitive_badge.label}
                          </span>
                        );
                      })()}
                      {agent.category && !agent.sensitive_badge && (
                        <span className="text-xs px-2 py-1 bg-[rgba(255,255,255,0.05)] text-gray-300 rounded h-fit">
                          {agent.category}
                        </span>
                      )}
                      {/* Phase C Fix: Use capability_profile as source of truth for voice badge */}
                      {(agent.capability_profile === 'live_voice_s2s_agent' || 
                        agent.capability_profile === 'voice_output_tts_agent' || 
                        agent.capability_profile === 'voice_input_agent' ||
                        agent.is_voice_template) && (
                        <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded h-fit">
                          {agent.capability_profile === 'live_voice_s2s_agent' ? '🎙️ Live Voice (S2S)' :
                           agent.capability_profile === 'voice_output_tts_agent' ? '🔊 Voice Output (TTS)' :
                           agent.capability_profile === 'voice_input_agent' ? '🗣️ Voice Input' :
                           '🎙️ Voice'}
                        </span>
                      )}
                    </div>

                    {/* Actions - always at bottom */}
                    <div className="flex gap-2 mt-auto">
                      <button
                        onClick={() => handleRunAgent(agent)}
                        className="flex-1 px-4 py-2 bg-[#6366F1] hover:bg-[#5558E6] text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Play size={16} />
                        Run
                      </button>
                      <button
                        onClick={() => navigate(`/agents/${agent.id}/dashboard`)}
                        className="px-4 py-2 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                        title="Dashboard"
                      >
                        <SquaresFour size={16} />
                      </button>
                      <button
                        onClick={() => navigate(`/unified-agent-builder/${agent.id}`)}
                        className="px-4 py-2 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                        title="Open in Builder"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleViewSessions(agent.id)}
                        className="px-4 py-2 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                        title="Sessions"
                      >
                        <ListBullets size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Operator Agents Section */}
        {operators.length > 0 && (
          <>
            <h2 className="text-xl font-semibold text-white mb-4">Operator Workers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {operators.map((operator) => (
                <div
                  key={operator.id}
                  className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)] hover:border-[#EC4899] transition-all group flex flex-col h-full"
                >
                  {/* Icon & Status */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-lg overflow-hidden">
                      <img 
                        src="/orion-icon-192.png" 
                        alt="Orion"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      operator.status === 'active' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {operator.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Name & Description */}
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#EC4899] transition-colors">
                    {operator.name}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2 flex-grow">
                    {operator.description || 'Operator agent for local machine control'}
                  </p>

                  {/* Type badge */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-xs px-2 py-1 bg-[#6366F1]/15 text-[#818CF8] border border-[#6366F1]/30 rounded">
                      Orion Operator
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => setSelectedOperatorForChat(operator)}
                      className="flex-1 px-4 py-2 bg-[#6366F1] hover:bg-[#5558E6] text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                      title="Chat with Operator"
                    >
                      <ChatText size={16} />
                      Chat
                    </button>
                    <button
                      onClick={() => navigate(`/partner-pro/ai-workforce/operators/${operator.id}`)}
                      className="px-4 py-2 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Run Agent Modal */}
        <RunAgentModal
          agent={selectedAgent}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={handleSessionSuccess}
        />

        {/* Operator Chat Modal */}
        <OperatorChatModal
          isOpen={!!selectedOperatorForChat}
          onClose={() => setSelectedOperatorForChat(null)}
          operator={selectedOperatorForChat}
        />
      </div>
    </div>
  );
};

export default MyAgents;
